type DroppedFileCollection = FileList | File[];

interface FileSystemEntryLike {
    isFile: boolean;
    isDirectory: boolean;
    name: string;
}

interface FileSystemFileEntryLike extends FileSystemEntryLike {
    file: (
        success: (file: File) => void,
        error?: (error: DOMException) => void
    ) => void;
}

interface FileSystemDirectoryReaderLike {
    readEntries: (
        success: (entries: FileSystemEntryLike[]) => void,
        error?: (error: DOMException) => void
    ) => void;
}

interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
    createReader: () => FileSystemDirectoryReaderLike;
}

function isFileEntry(entry: FileSystemEntryLike): entry is FileSystemFileEntryLike {
    return entry.isFile && 'file' in entry && typeof entry.file === 'function';
}

function isDirectoryEntry(entry: FileSystemEntryLike): entry is FileSystemDirectoryEntryLike {
    return entry.isDirectory && 'createReader' in entry && typeof entry.createReader === 'function';
}

function readFileEntry(entry: FileSystemFileEntryLike): Promise<File> {
    return new Promise((resolve, reject) => {
        entry.file(resolve, reject);
    });
}

async function readDirectoryEntries(directory: FileSystemDirectoryEntryLike): Promise<FileSystemEntryLike[]> {
    const reader = directory.createReader();
    const entries: FileSystemEntryLike[] = [];

    while (true) {
        const batch = await new Promise<FileSystemEntryLike[]>((resolve, reject) => {
            reader.readEntries(resolve, reject);
        });
        if (batch.length === 0) break;
        entries.push(...batch);
    }

    return entries;
}

async function readEntryFiles(entry: FileSystemEntryLike): Promise<File[]> {
    if (isFileEntry(entry)) {
        return [await readFileEntry(entry)];
    }

    if (!isDirectoryEntry(entry)) {
        return [];
    }

    const childEntries = await readDirectoryEntries(entry);
    const nestedFiles = await Promise.all(childEntries.map(readEntryFiles));
    return nestedFiles.flat();
}

function getEntryFromItem(item: DataTransferItem): FileSystemEntryLike | null {
    const itemWithEntry = item as {
        webkitGetAsEntry?: () => FileSystemEntryLike | null;
    };
    return itemWithEntry.webkitGetAsEntry?.() ?? null;
}

export async function getFilesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
    const filesFromEntries: File[] = [];

    for (const item of Array.from(dataTransfer.items ?? [])) {
        if (item.kind !== 'file') continue;

        const entry = getEntryFromItem(item);
        if (entry) {
            filesFromEntries.push(...await readEntryFiles(entry));
            continue;
        }

        const file = item.getAsFile();
        if (file) filesFromEntries.push(file);
    }

    if (filesFromEntries.length > 0) return filesFromEntries;
    return Array.from(dataTransfer.files ?? []);
}

export function toFileArray(files: DroppedFileCollection): File[] {
    return Array.from(files);
}

export type { DroppedFileCollection };
